<mat-drawer-container class="drawer-container" [ngStyle]="{'height': drawerHeight}">
  <lib-cdw-drawer
    [drawerWidth]="drawerWidth"
    [drawerId]="drawerId"
    [toolbarIconName]="drawerIcon"
    [toolbarTitle]="drawerTitleLine || drawerTitle"
    [toolbarTitleBackground]="toolbarTitleBackground"
    [toolbarTitleColor]="toolbarTitleColor"
    [toolbarTitleFontFamily]="toolbarTitleFontFamily"
    [toolbarTitleFontSize]="toolbarTitleFontSize"
    [toolbarTitleFontWeight]="toolbarTitleFontWeight"
    [hideHeaderBorder]="hideHeaderBorder"
    [interceptCloseButton]="true"
    (closeRequested)="attemptCancel()">
    <lib-cdw-stepper class="h-full" orientation="vertical" [disableHeaderButtons]="true" #bulkUpdateStepper completedStepIcon="check">
      <!-- Deprovision Acknowledgement Step -->
      <lib-cdw-step *ngIf="actionType === 'deprovision'" [label]="actionLabels.steps[0]">
        <div class="deprovision-content mb-6">
          <lib-cdw-message-band mode="error-soft" icon="warning">
            Deprovisioning removes licenses and the selected devices would need to be re-enrolled.
            <br><br>
            Are you sure you want to proceed?
          </lib-cdw-message-band>

          <p>Consider disabling the devices first, which will retain their licenses. You can always deprovision them later.</p>

          <p>If you'd like to continue deprovisioning the selected devices, this action will deprovision <strong>{{effectiveSelectedCount}} device{{effectiveSelectedCount === 1 ? '' : 's'}}</strong>, remove licenses, and remove the devices from management. It applies to inactive, resold, or devices submitted for return/repair, requiring wiping before re-enrollment.</p>

          <lib-cdw-divider class="deprovision-divider"></lib-cdw-divider>

          <div class="acknowledgement-checkbox">
            <lib-cdw-checkbox [(ngModel)]="deprovisionAcknowledged">
              I acknowledge the need to wipe and re-enroll these devices for future management.
            </lib-cdw-checkbox>
          </div>
        </div>
        <div class="button-container">
          <lib-cdw-button class="cancel-button" mode="small" level="warn" (buttonClick)="reset()">Cancel</lib-cdw-button>
          <lib-cdw-button class="next-button" mode="small" level="primary" (buttonClick)="bulkUpdateStepper.nextStep()" [disabled]="!deprovisionAcknowledged">Next</lib-cdw-button>
        </div>
      </lib-cdw-step>

      <!-- Deprovision Reason Step -->
      <lib-cdw-step *ngIf="actionType === 'deprovision'" label="Specify deprovisioning reason">
        <div class="deprovision-content mb-6">
          <p>Specify a deprovisioning reason according to license policy guidelines. Familiarize yourself with appropriate deprovisioning reasons and their usage before proceeding.</p>

          <mat-radio-group [(ngModel)]="deprovisionReason" class="deprovision-reasons">
            <mat-radio-button *ngFor="let reason of deprovisionReasons" [value]="reason.value" class="deprovision-reason-option">
              {{reason.label}}
            </mat-radio-button>
          </mat-radio-group>
        </div>
        <div class="button-container">
          <lib-cdw-button class="cancel-button" mode="small" level="warn" (buttonClick)="reset()">Cancel</lib-cdw-button>
          <lib-cdw-button class="back-button" mode="small" level="secondary" (buttonClick)="previousStep()">Back</lib-cdw-button>
          <lib-cdw-button class="next-button" mode="small" level="primary" (buttonClick)="completeStatusOnlyActionStep()" [disabled]="!deprovisionReason">Next</lib-cdw-button>
        </div>
      </lib-cdw-step>

      <!--
        Generic Confirm step driven by the host's confirmArchetype registration.
        Deprovision has its own dedicated 2-step block above (Acknowledge + Reason).
        - 'prose'                → informational text only
        - 'warning'              → warning band + prose, no checkbox
        - 'warning-acknowledge'  → warning band + prose + acknowledge checkbox
        - 'none'                 → no Confirm step rendered
      -->
      <lib-cdw-step
        *ngIf="confirmArchetype !== 'none' && actionType !== 'deprovision'"
        [label]="confirmStepLabel">
        <div class="confirmation-content" style="margin-bottom: 20px;">
          <lib-cdw-message-band
            *ngIf="confirmArchetype === 'warning' || confirmArchetype === 'warning-acknowledge'"
            mode="warning-soft"
            icon="warning">
            <span [innerHTML]="warningBandHtml"></span>
          </lib-cdw-message-band>
          <p *ngIf="confirmProse" [innerHTML]="confirmProse"></p>
          <div *ngIf="confirmRequiresAcknowledge" class="acknowledgement-checkbox">
            <lib-cdw-checkbox [(ngModel)]="confirmAcknowledged">
              {{ acknowledgeLabel }}
            </lib-cdw-checkbox>
          </div>
        </div>
        <lib-cdw-message-band
          *ngIf="crossPageReminderText"
          mode="info"
          icon="info"
          class="cross-page-reminder">
          {{ crossPageReminderText }}
        </lib-cdw-message-band>
        <div class="button-container">
          <lib-cdw-button class="cancel-button" mode="small" level="warn" (buttonClick)="reset()">Cancel</lib-cdw-button>
          <lib-cdw-button
            class="next-button"
            mode="small"
            level="primary"
            (buttonClick)="completeStatusOnlyActionStep()"
            [disabled]="(confirmRequiresAcknowledge && !confirmAcknowledged) || isNextButtonDisabled">
            Next
          </lib-cdw-button>
        </div>
      </lib-cdw-step>

      <!-- Field Selection Step (hidden for status-only actions) -->
      <lib-cdw-step *ngIf="!isStatusOnlyAction" [label]="effectiveSelectedCount + ' ' + (effectiveSelectedCount === 1 ? 'Item' : 'Items') + ' Selected'">
        <div class="fields-container">
          <lib-cdw-bulk-field-update
          *ngFor="let fieldUpdateConfig of filteredFieldUpdateConfigs"
          #fieldUpdate
          class="field-update"
          [fieldUpdateConfig]="fieldUpdateConfig"
          [deviceMetadataValues]="deviceMetadataForValidation"
          (fieldUpdateChange)="onFieldUpdateChange()"
          (validationStateChange)="onFieldValidationStateChange($event)"></lib-cdw-bulk-field-update>
        </div>
        <!-- EDUS-2559: warn + require acknowledgment before a blank Replace
             clears existing data on every selected row. -->
        <div *ngIf="hasBlankReplace" class="blank-replace-warning">
          <lib-cdw-message-band mode="warning-soft" icon="warning">
            Blank values with replace operation will be cleared
          </lib-cdw-message-band>
          <div class="acknowledgement-checkbox">
            <lib-cdw-checkbox [(ngModel)]="blankReplaceAcknowledged">
              I acknowledge that leaving the value blank with the replace operation will clear all data from the selected cells on {{ effectiveSelectedCount }} item{{ effectiveSelectedCount === 1 ? '' : 's' }}, and this action cannot be undone.
            </lib-cdw-checkbox>
          </div>
        </div>
        <lib-cdw-message-band
          *ngIf="crossPageReminderText"
          mode="info"
          icon="info"
          class="cross-page-reminder">
          {{ crossPageReminderText }}
        </lib-cdw-message-band>
        <div class="button-container">
          <lib-cdw-button class="cancel-button" mode="small" level="warn" (buttonClick)="attemptCancel()">Cancel</lib-cdw-button>
          <lib-cdw-button class="next-button" mode="small" level="primary" (buttonClick)="completeStep1()" [disabled]="isNextButtonDisabled || hasValidationErrors || (hasBlankReplace && !blankReplaceAcknowledged)">Next</lib-cdw-button>
        </div>
      </lib-cdw-step>

      <lib-cdw-step label="Summary">
        <ng-container *ngIf="eligibility; else legacySummaryHeader">
          <div class="summary-subtitle" [innerHTML]="'<strong>' + eligibility.totals.selected + ' device' + (eligibility.totals.selected === 1 ? '' : 's') + '</strong> have been selected for modification'"></div>
          <!-- Action-specific pending line (e.g. "queue {Y} for a profile wipe") -->
          <div *ngIf="eligibility.totals.qualified > 0" class="summary-pending-line">
            <lib-cdw-icon icon="timelapse" class="summary-pending-icon"></lib-cdw-icon>
            <span [innerHTML]="summaryPendingLine"></span>
          </div>
        </ng-container>
        <ng-template #legacySummaryHeader>
          <div [innerHTML]="summaryPendingLine"></div>
        </ng-template>
        <!-- Command-only actions have no per-field summary table;
             hide when all devices are skipped (degenerate case per Figma). -->
        <div class="table-container" *ngIf="!isCommandOnlyAction && (!eligibility || eligibility.totals.qualified > 0)">
          <table class="summary-table" mat-table [dataSource]="fieldUpdateSummary">
            <ng-container matColumnDef="field">
              <th mat-header-cell *matHeaderCellDef>Field</th>
              <td mat-cell *matCellDef="let update"> {{update.name}} </td>
            </ng-container>

            <ng-container matColumnDef="type">
              <th mat-header-cell *matHeaderCellDef> Operation </th>
              <td mat-cell *matCellDef="let update"> {{update.updateType}} </td>
            </ng-container>

            <ng-container matColumnDef="value">
              <th mat-header-cell *matHeaderCellDef> Value </th>
              <td mat-cell *matCellDef="let update"> {{update.value}} </td>
            </ng-container>

            <tr mat-header-row *matHeaderRowDef="fieldUpdateSummaryColumns"></tr>
            <tr mat-row *matRowDef="let row; columns: fieldUpdateSummaryColumns;"></tr>
          </table>
        </div>

        <!-- EDUS-2163: per-reason skip breakdown — covers both partial-skip and
             degenerate (all skipped) cases. Band text varies; skip table and
             save-link render for both per Figma. -->
        <ng-container *ngIf="eligibility && eligibility.totals.skipped > 0">
          <lib-cdw-message-band
            *ngIf="eligibility.totals.qualified > 0"
            mode="warning-soft">
            <strong>{{ eligibility.totals.skipped }} device(s)</strong> will be skipped.
            <a *ngIf="eligibility.seeMoreInfoUrl" [href]="eligibility.seeMoreInfoUrl" target="_blank" rel="noopener">See more info</a>
          </lib-cdw-message-band>
          <lib-cdw-message-band
            *ngIf="eligibility.totals.qualified === 0"
            mode="error-soft"
            icon="warning">
            No device{{ eligibility.totals.selected === 1 ? '' : 's' }} selected are eligible for this action.
            <a *ngIf="eligibility.seeMoreInfoUrl" [href]="eligibility.seeMoreInfoUrl" target="_blank" rel="noopener">See more info</a>
          </lib-cdw-message-band>
          <table class="skipped-table">
            <thead>
              <tr>
                <th>Devices Skipped</th>
                <th>Reason</th>
              </tr>
            </thead>
            <tbody>
              <tr *ngFor="let group of eligibility.skipGroups">
                <td>{{ group.count }}</td>
                <td>{{ group.label }}</td>
              </tr>
            </tbody>
          </table>
          <lib-cdw-button
            class="save-list-to-cloud-link"
            mode="basic"
            level="primary"
            icon="cloud_upload"
            (buttonClick)="onExportSkippedDevices()">
            Export skipped device list
          </lib-cdw-button>
        </ng-container>

        <div class="button-container">
          <lib-cdw-button mode="small" level="warn" class="cancel-button" (buttonClick)="attemptCancel()">Cancel</lib-cdw-button>
          <lib-cdw-button mode="small" level="secondary" class="back-button" (buttonClick)="previousStep()">Back</lib-cdw-button>
          <lib-cdw-button mode="small" level="primary" class="next-button" (buttonClick)="executeBulkUpdate()" [disabled]="!!eligibility && eligibility.totals.qualified === 0">{{ actionLabels.summaryActionButtonLabel || 'Execute' }}</lib-cdw-button>
        </div>
      </lib-cdw-step>
      <!-- Stepper labels come from per-action label map (e.g. "Queue Job" for profile-wipe) -->
      <lib-cdw-step [label]="actionLabels.steps[progressStepIndex]">
        <div *ngIf="!bulkJob && !isCancellingJob">
          <div>Initializing {{ actionLabels.progressTitle }}</div>
          <lib-cdw-progress-bar></lib-cdw-progress-bar>
        </div>
        <div *ngIf="bulkJob && !isCancellingJob">
          <div>{{ actionLabels.progressTitle }}</div>
          <lib-cdw-progress-bar></lib-cdw-progress-bar>
          <div>{{bulkJob.completedItems}} of <strong>{{bulkJob.totalItems}}</strong> completed</div>
        </div>
        <div *ngIf="isCancellingJob">
          <div>Cancelling {{ actionLabels.progressTitle }}</div>
          <lib-cdw-progress-bar></lib-cdw-progress-bar>
        </div>
        <div class="button-container">
          <lib-cdw-button mode="small" level="secondary" class="close-progress-button" (buttonClick)="reset()">Close</lib-cdw-button>
        </div>
      </lib-cdw-step>
      <lib-cdw-step [label]="actionLabels.steps[completedStepIndex]">
        <!-- Per-action success copy (e.g. "queued for a profile wipe") -->
        <div *ngIf="bulkJob && bulkJob.status === 'completed'" class="completed-success-line">
          <lib-cdw-icon icon="check_circle" class="completed-success-icon" customColor="#2e7d32"></lib-cdw-icon>
          <span [innerHTML]="completedTitleLine"></span>
        </div>
        <div *ngIf="bulkJob && bulkJob.status === 'cancelled'">Bulk edit cancelled successfully</div>
        <div *ngIf="bulkJob && bulkJob.status === 'error'">
          <div>An error occurred while performing the bulk edit</div>
          <div *ngIf="bulkJob.error" class="error-message">{{bulkJob.error}}</div>
        </div>
        <!-- Surface skipped count on the Completed step (per-action wording from label map) -->
        <div *ngIf="eligibility && eligibility.totals.skipped > 0" class="completed-skipped-line">
          <lib-cdw-icon icon="info" class="completed-skipped-icon"></lib-cdw-icon>
          <span>
            <span [innerHTML]="completedSkippedLine"></span>
            <a *ngIf="eligibility.seeMoreInfoUrl" class="completed-see-more-link" [href]="eligibility.seeMoreInfoUrl" target="_blank" rel="noopener">See more info</a>
          </span>
        </div>
        <div class="button-container">
          <lib-cdw-button mode="small" level="primary" (buttonClick)="reset()">Close</lib-cdw-button>
        </div>
      </lib-cdw-step>
    </lib-cdw-stepper>
  </lib-cdw-drawer>
  <ng-content></ng-content>
</mat-drawer-container>